import type { Language } from '@kratispot/shared';

const LOCALES: Readonly<Record<Language, string>> = { el: 'el-GR', en: 'en-GB' };

export function localeOf(language: Language): string {
  return LOCALES[language];
}

// Days are calendar days; noon UTC keeps every timezone on the intended date.
function dayOf(date: string): Date {
  return new Date(`${date}T12:00:00Z`);
}

export function weekdayOf(date: string, language: Language): string {
  return dayOf(date).toLocaleDateString(localeOf(language), { weekday: 'short' });
}

export function dayMonthOf(date: string, language: Language): string {
  return dayOf(date).toLocaleDateString(localeOf(language), { day: 'numeric', month: 'short' });
}

// What a date field shows: iOS's own format, so nothing reads as a downgrade.
export function dateFieldOf(date: string, language: Language): string {
  return dayOf(date).toLocaleDateString(localeOf(language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function fullDayOf(date: string, language: Language): string {
  return dayOf(date).toLocaleDateString(localeOf(language), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

// This device's own clock, not a venue's: when somebody was last here. §18
export function editedAt(instant: string, language: Language): string {
  return new Date(instant).toLocaleString(localeOf(language), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// The venue's own clock, on a 24-hour dial: a beach shift is not am and pm.
export function timeOf(instant: string, timezone: string, language: Language): string {
  return new Date(instant).toLocaleTimeString(localeOf(language), {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function moneyOf(cents: number, currency: string, language: Language): string {
  return (cents / 100).toLocaleString(localeOf(language), { style: 'currency', currency });
}

// Inputs are edited in the currency's major unit; storage is always cents.
export function majorOf(cents: number): string {
  return (cents / 100).toFixed(2).replace(/\.00$/, '');
}

// A blank or unreadable field is nothing owed, never a NaN sent to the API.
export function centsOf(text: string): number {
  const major = Number.parseFloat(text.replace(',', '.'));
  return Number.isFinite(major) && major > 0 ? Math.round(major * 100) : 0;
}

// A whole-number field: hours before arrival, or a percentage of the price.
export function wholeOf(text: string): number {
  const value = Number.parseInt(text, 10);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

// The venue's own currency as its symbol alone, in the language on screen. §9
export function currencySymbolOf(currency: string, language: Language): string {
  return new Intl.NumberFormat(localeOf(language), { style: 'currency', currency })
    .formatToParts(0)
    .filter((part) => part.type === 'currency')
    .map((part) => part.value)
    .join('');
}
